import type { ApiErrorCode } from '@privisy/api-types';
import { API_ERROR_CODES } from '@privisy/api-types';

/**
 * ApiError — the single error type every api.ts call rejects with (#704).
 * Lives outside api.ts so presentation code can `instanceof` it in suites
 * that jest.mock('../utils/api') with a hand-written factory.
 */
export interface ApiErrorBody {
  /** Machine-readable code the backend puts in `error`, e.g. 'insufficient_credits'. */
  error?: string;
  /** Typed error code (#1695), e.g. 'SCAN_ALREADY_CLAIMED'. Switch on this, never on prose. */
  code?: string;
  /** Human-readable sentence the backend puts in `message`, when it sends one. */
  message?: string;
  [key: string]: unknown;
}

const KNOWN_CODES: ReadonlySet<string> = new Set(API_ERROR_CODES);

export class ApiError extends Error {
  status: number;
  /** The body's `error` string (e.g. 'insufficient_credits'); kept for existing callers. */
  code: string;
  /** The body's typed `code` (#1695), when it is one the contract knows. */
  apiCode?: ApiErrorCode;
  body: ApiErrorBody;
  constructor(message: string, status: number, code = '', body: ApiErrorBody = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
    if (typeof body.code === 'string' && KNOWN_CODES.has(body.code)) {
      this.apiCode = body.code as ApiErrorCode;
    }
  }
}

/** Claim failures are ApiErrors with a distinct name; useClaimScan branches on it. */
export class ClaimError extends ApiError {
  constructor(message: string, status: number, code = '', body: ApiErrorBody = {}) {
    super(message, status, code, body);
    this.name = 'ClaimError';
  }
}

/**
 * The single source of truth for "is this the out-of-credits 402" (#943
 * fix round 1). The call sites that branched on it (App.tsx's handleScan,
 * and the post-claim screen removed in #1784) used to spell out
 * `instanceof ApiError && status === 402 && code === 'insufficient_credits'`
 * independently. That duplication is a #874 leak waiting to happen: if one
 * copy drifts from the other, that 402 falls through to ApiErrorNotice's
 * generic branch and renders the raw machine code. Call this instead of
 * re-deriving the check.
 */
export function isInsufficientCredits(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 402 && err.code === 'insufficient_credits';
}
